import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon
  const [meta, setMeta] = useState({ activeTenantId: null, tenantRole: null, platformRole: null, memberships: [], permissions: [], platformPermissions: [], modules: [], businessTypes: ["general"], businessCapabilities: {} });

  const applyMe = (data) => {
    setUser(data.user);
    setMeta({
      activeTenantId: data.active_tenant_id, tenantRole: data.tenant_role,
      platformRole: data.platform_role, memberships: data.memberships || [],
      permissions: data.permissions || [], platformPermissions: data.platform_permissions || [],
      modules: data.modules || [],
      businessTypes: data.business_types || ["general"], businessCapabilities: data.business_capabilities || {},
    });
  };

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", { _skipAuthRedirect: true });
      applyMe(data);
    } catch (e) {
      // Access cookie may be expired; try a single silent refresh before giving up.
      try {
        await api.post("/auth/refresh", {}, { _skipAuthRedirect: true });
        const { data } = await api.get("/auth/me", { _skipAuthRedirect: true });
        applyMe(data);
      } catch (e2) {
        setUser(null);
      }
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = useCallback(async (email, password, tenantId) => {
    const { data } = await api.post("/auth/login", { email, password, tenant_id: tenantId }, { _skipAuthRedirect: true });
    if (data.mfa_required) return data;
    await loadMe();
    return data;
  }, [loadMe]);

  const verifyMfa = useCallback(async (challengeToken, code) => {
    const { data } = await api.post("/auth/mfa/login", { challenge_token: challengeToken, code }, { _skipAuthRedirect: true });
    await loadMe();
    return data;
  }, [loadMe]);

  const switchTenant = useCallback(async (tenantId) => {
    await api.post("/auth/switch-tenant", { tenant_id: tenantId });
    await loadMe();
  }, [loadMe]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout request failed", e); }
    setUser(null);
    window.location.href = "/login";
  }, []);

  const updatePreferences = useCallback(async (prefs) => {
    const { data } = await api.patch("/auth/preferences", prefs);
    setUser(data);
    return data;
  }, []);

  const can = useCallback((perm) => meta.permissions.includes(perm), [meta.permissions]);
  const canPlatform = useCallback((perm) => meta.platformPermissions.includes(perm), [meta.platformPermissions]);

  return (
    <AuthContext.Provider value={{ user, ...meta, login, verifyMfa, logout, switchTenant, loadMe, updatePreferences, can, canPlatform }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
